import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';
import { AdminTestStatus } from './admin-test-status.model';

/**
 * A safety test as `/admin/safety-tests` returns it (hub `data-models/safety-test.ts`; the list adds
 * `questionCount`). Status changes go through activate / new-version and the study guide through its own route.
 * A writable field the hub didn't send stays `undefined`.
 */
export class SafetyTest extends Base {
  // Writable via PUT /admin/safety-tests/:id (the hub's SAFETY_TEST_WRITABLE)
  categoryId?: string | null;
  title?: string | null;
  shortName?: string | null;
  description?: string | null;
  versionNotes?: string | null;
  /** ISO date as sent by the hub, so it round-trips unchanged. */
  effectiveDate?: string | null;
  passingScore?: number | null;
  timeLimit?: number | null;
  questionCount?: number | null;
  shuffleQuestions?: boolean | null;
  shuffleOptions?: boolean | null;
  expirationDays?: number | null;
  expiringSoonDays?: number | null;

  // Read-only
  organizationId?: string | null;
  status?: AdminTestStatus | null;
  version?: number | null;
  replacesId?: string | null;
  replacedById?: string | null;
  studyGuideUrl?: string | null;
  studyGuideFilename?: string | null;
  isCustom?: boolean | null;
  createdBy?: string | null;
  updatedBy?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.readWritableFields(source);
    this.organizationId = nullableString(source['organizationId']);
    this.status = nullableString(source['status']) as AdminTestStatus | null | undefined;
    this.version = nullableNumber(source['version']);
    this.replacesId = nullableString(source['replacesId']);
    this.replacedById = nullableString(source['replacedById']);
    this.studyGuideUrl = nullableString(source['studyGuideUrl']);
    this.studyGuideFilename = nullableString(source['studyGuideFilename']);
    this.isCustom = nullableBoolean(source['isCustom']);
    this.createdBy = nullableString(source['createdBy']);
    this.updatedBy = nullableString(source['updatedBy']);
  }

  private readWritableFields(source: Record<string, unknown>): void {
    this.categoryId = nullableString(source['categoryId']);
    this.title = nullableString(source['title']);
    this.shortName = nullableString(source['shortName']);
    this.description = nullableString(source['description']);
    this.versionNotes = nullableString(source['versionNotes']);
    this.effectiveDate = nullableString(source['effectiveDate']);
    this.passingScore = nullableNumber(source['passingScore']);
    this.timeLimit = nullableNumber(source['timeLimit']);
    this.questionCount = nullableNumber(source['questionCount']);
    this.shuffleQuestions = nullableBoolean(source['shuffleQuestions']);
    this.shuffleOptions = nullableBoolean(source['shuffleOptions']);
    this.expirationDays = nullableNumber(source['expirationDays']);
    this.expiringSoonDays = nullableNumber(source['expiringSoonDays']);
  }
}
